import api, {formatAPIURL} from '../../../common/api.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import {
  setForm,
  updateForm,
  removeForm,
  onlyIfForm,
  getState,
  useSelector,
} from '../../../store.js'
import {formsSelector} from '../../../redux/selectors/ui/forms.js'
import {refreshPurchaseOrderList} from '../purchaseOrderListActions.js'
import {
  Count,
  IfPlural,
  IfSingle,
  plural,
  Plural,
  PluralBlock,
} from '../../../common/components/Plural.js'
import {suppliersWithoutPOEmailSelector, setPO} from '../../../data/pos.js'
import {getSupplierName} from '../../../data/suppliers.js'
import ButtonLink from '../../../common/components/Button/ButtonLink.js'
import {navigate} from '../../../common/location.js'
import Checkbox from '../../../common/components/Checkbox.js'
import {saveUserSettings, userSettingSelector} from '../../../data/me.js'
import {buildPath} from '../../../common/querystring.js'

export const MODAL_FORM = 'send_to_supplier_modal'
const PO__SUPPLIER__SHOW_IMAGES = 'po.supplier.show_images'
const PO__SUPPLIER__SHOW_SUPPLIER_NOTE = 'po.supplier.show_supplier_note'

export function showPOSupplierShowImagesSelector(state) {
  return userSettingSelector(state, {
    setting: PO__SUPPLIER__SHOW_IMAGES,
    defaultValue: false,
  })
}

export function showPOSupplierShowSupplierNoteSelector(state) {
  return userSettingSelector(state, {
    setting: PO__SUPPLIER__SHOW_SUPPLIER_NOTE,
    defaultValue: false,
  })
}

export function showSendToSupplierModal(poIDs, {preview = false} = {}) {
  setForm(MODAL_FORM, {
    poIDs,
    preview,
    show_images: showPOSupplierShowImagesSelector(getState()),
    show_supplier_note: showPOSupplierShowSupplierNoteSelector(getState()),
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

async function saveSupplierModalSettings() {
  const {show_images, show_supplier_note} = modalFormSelector(getState())

  await saveUserSettings({
    [PO__SUPPLIER__SHOW_IMAGES]: show_images,
    [PO__SUPPLIER__SHOW_SUPPLIER_NOTE]: show_supplier_note,
  })
}

function supplierEmailParamsSelector(state) {
  const {show_images, show_supplier_note} = modalFormSelector(state)

  return {
    show_images,
    show_supplier_note,
  }
}

export async function sendPO(id, params) {
  const {json} = await api.post(
    `/purchase_order/${encodeURIComponent(id)}/send`,
    params,
  )

  setPO(json)
}

export function previewPO() {
  const {poIDs} = modalFormSelector(getState())
  const params = supplierEmailParamsSelector(getState())

  const printURL = formatAPIURL(
    buildPath(['purchase_order', poIDs[0], 'send']),
    params,
  )

  window.open(printURL, '_blank')

  saveSupplierModalSettings()

  closeModal()
}

export async function sendToSupplier() {
  try {
    updateModalForm({isSaving: true, serverError: null})

    const {poIDs} = modalFormSelector(getState())

    const params = supplierEmailParamsSelector(getState())

    await Promise.all(poIDs.map((id) => sendPO(id, params)))

    await saveSupplierModalSettings()

    showMessageToast(
      plural(poIDs)`${Count} PO${['s were', ' was']} successfully emailed!`,
    )

    closeModal()

    await refreshPurchaseOrderList()
  } catch (err) {
    updateModalForm({
      serverError: `Error sending PO to supplier: ${
        err.message || err.error_message
      }`,
      isSaving: false,
    })
  }
}

export function navigateToSupplierSettings(supplierID) {
  closeModal()

  navigate(['settings', 'supplier', supplierID])
}

function SendToSupplierModal({form}) {
  const supplierWithoutPOEmail = useSelector((state) =>
    suppliersWithoutPOEmailSelector(state, {poIDs: form.poIDs}),
  )

  const cantSend = supplierWithoutPOEmail.length > 0

  return (
    <ConfirmModal
      title={plural(
        form.poIDs,
      )`${form.preview ? 'Preview' : 'Send'} PO${['s']} to Supplier${['s']}`}
      onConfirm={form.preview ? previewPO : cantSend ? null : sendToSupplier}
      onCancel={() => closeModal()}
      isSaving={form.isSaving}
      error={form.serverError}
      confirmText={form.preview ? 'Preview' : 'Yes'}
      cancelText={cantSend ? 'Cancel' : 'No'}
    >
      {!form.preview && cantSend ? (
        <PluralBlock array={supplierWithoutPOEmail}>
          <IfPlural>
            <>
              <div className="margin-bottom-15">
                <strong>
                  <Count /> suppliers do not have PO email addresses.
                </strong>{' '}
              </div>
              <div>
                PO requests cannot be sent until email addresses have been
                configured.
              </div>
            </>
          </IfPlural>
          <IfSingle>
            <>
              <div className="margin-bottom-15">
                <strong>{getSupplierName(supplierWithoutPOEmail[0])}</strong>{' '}
                does not have a PO email address assigned to it.
              </div>

              <div>
                <ButtonLink
                  className="mid border-underline v-align-base"
                  onClick={() =>
                    navigateToSupplierSettings(supplierWithoutPOEmail[0].id)
                  }
                >
                  <strong>Add a PO email address for this supplier</strong>
                </ButtonLink>{' '}
                →
              </div>
            </>
          </IfSingle>
        </PluralBlock>
      ) : (
        <>
          <p>
            {form.preview ? (
              <>Preview supplier email for PO {form.poIDs[0]}</>
            ) : (
              <PluralBlock array={form.poIDs}>
                Send <Count /> <Plural word="PO" /> to your{' '}
                <Plural word="supplier" />?
              </PluralBlock>
            )}
          </p>
          <Checkbox
            labelClassName="margin-bottom-10"
            id="send_po_show_images"
            label="Show product images"
            checked={form.show_images}
            onChange={(show_images) => updateModalForm({show_images})}
          />
          <Checkbox
            id="send_po_show_supplier_note"
            label="Show supplier notes for SKUs"
            checked={form.show_supplier_note}
            onChange={(show_supplier_note) =>
              updateModalForm({show_supplier_note})
            }
          />
        </>
      )}
    </ConfirmModal>
  )
}

export default onlyIfForm(SendToSupplierModal, modalFormSelector)
